import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import styled from "styled-components";
import { IconButton, ProfileDisplay } from "../../../components";
import { ConfirmModal } from "../../../components/modals";
import { StandardTableSortablePropertyHeader, ThemedTableHeader } from "../../../components/tables/StandardTable";
import VirtualTable, { TableLoadingRow, TableRecordCountDisplayFooter } from "../../../components/tables/VirtualTable";
import { useClientSetContext } from "../hooks/ClientSetContext";
import useClientSetAccountsGrid from "../hooks/useClientSetAccountsGrid";
import AddMasterAccountModal from "./AddMasterAccountModal";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";

const TableCell = styled.td`
    width: ${({ width }) => width}%;
    max-width: 50px;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ClientSetAccountsDropdownToggle = React.forwardRef(({ onClick }, ref) => (
    <FontAwesomeIcon
        ref={ref}
        className="has-pointer"
        icon="fa-ellipsis"
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
    />
));

const ClientSetAccountsRow = ({ data, index, style, isLoading, setRemoveMasterAccount, isEditing }) => {
    const { height } = style;

    const {
        isDeleting
    } = useClientSetContext();

    if (isLoading)
        return <></>;

    if (index >= data.length)
        return <TableLoadingRow height={height} />;

    const {
        adviserId,
        masterAccountReference,
        masterAccountId,
        masterAccountName,
        targetProfileName,
        standardPortfolioName
    } = data[index];

    return <tr index={index} style={{ height }}>
        <TableCell width={15}>
            {adviserId == null ? "Not Set" : <ProfileDisplay
                userId={adviserId}
            />}
        </TableCell>
        <TableCell width={10}>
            {masterAccountReference}
        </TableCell>
        <TableCell width={30}>
            <a href={`/client/${masterAccountId}`} className="text-truncate">
                {masterAccountName}
            </a>
        </TableCell>
        <TableCell width={17.5}>
            {targetProfileName ?? "Not Set"}
        </TableCell>
        <TableCell width={27.5}>
            {standardPortfolioName ?? "Bespoke"}
        </TableCell>
        <td className="action-col text-center">
            <Dropdown className="caret-off">
                <Dropdown.Toggle className="caret-off" as={ClientSetAccountsDropdownToggle} disabled={isEditing || isDeleting} />
                <Dropdown.Menu>
                    {/* <Dropdown.Header>{masterAccountName}</Dropdown.Header> */}
                    <Dropdown.Item disabled={isEditing} onClick={() => setRemoveMasterAccount(data[index])}>Remove client from set</Dropdown.Item>
                    {/* TODO: More client options */}
                </Dropdown.Menu>
            </Dropdown>
        </td>
    </tr>;
}

const ClientSetAccountsHeader = ({ currentSort, onClickSort, showAddModal }) => {
    const SortableHeader = ({ property, label }) => <StandardTableSortablePropertyHeader
        property={property}
        className="text-truncate"
        sort={currentSort ?? ""}
        sortTable={onClickSort}
    >
        {label}
    </StandardTableSortablePropertyHeader>

    return <thead>
        <tr>
            <SortableHeader property="adviserName" label="Adviser" />
            <SortableHeader property="masterAccountReferenceId" label="Ref" />
            <SortableHeader property="masterAccountName" label="Account Name" />
            <SortableHeader property="targetProfileId" label="Target Profile" />
            <SortableHeader property="standardPortfolioName" label="Portfolio" />
            <ThemedTableHeader className="text-center">
                <IconButton
                    icon="fa-plus"
                    variant="success"
                    size="sm"
                    onClick={showAddModal}
                />
            </ThemedTableHeader>
        </tr>
    </thead>;
}

const ClientSetAccountsGrid = () => {
    const {
        results,
        totalCount,
        sort,
        onClickSort,
        isClearing,
        isError,
        error,
        clear,
        loadMore,
        isRowLoaded,
        editAccounts,
        isEditing
    } = useClientSetAccountsGrid();

    // Remove client modal
    const showRemoveMasterAccountModal = ({ masterAccountId, masterAccountName }) => {
        openConfirmModal({
            dialogTitle: "Are you sure?",
            message: `Are you sure you want to remove ${masterAccountName} from this client set?`,
            confirmButtonText: "Remove Client",
            cancelButtonText: "Cancel",
            confirmButtonVariant: "danger"
        }).then(
            (proceed) => {
                if (proceed)
                    editAccounts({ masterAccountId });
            }
        )
    }

    const [showAddModal, setShowAddModal] = useState(false);
    const show = () => setShowAddModal(true);
    const hide = () => setShowAddModal(false);

    return <>
        <AddMasterAccountModal
            show={showAddModal}
            onHide={hide}
            addAccount={(masterAccountId) => {
                hide();
                editAccounts({ masterAccountId, add: true });
            }}
        />
        <VirtualTable
            enableScrollTracking
            row={(props) => <ClientSetAccountsRow
                isLoading={isClearing}
                isEditing={isEditing}
                setRemoveMasterAccount={showRemoveMasterAccountModal}
                {...props}
            />}
            header={<ClientSetAccountsHeader
                currentSort={sort}
                onClickSort={onClickSort}
                showAddModal={show}
            />}
            footer={(
                <TableRecordCountDisplayFooter
                    totalCount={isError ? 0 : totalCount}
                    isNewRecordsDisplayed={false}
                    newRecordsSinceCache={0}
                    reload={clear}
                    spanLeft={5}
                    spanRight={1}
                />
            )}
            error={error}
            isLoading={isClearing}
            isItemLoaded={isRowLoaded}
            loadMoreItems={loadMore}
            itemSize={40}
            itemCount={isError ? 0 : totalCount === results.length ? totalCount : results.length + 3}
            itemData={isError ? [] : results}
        />
    </>;
}

export default ClientSetAccountsGrid;